import {
  Button,
  FlexBox,
  Modal,
  Qrcode,
  space,
  Tag,
  theme,
  Typography,
} from "@waldner-laboreinrichtungen/component-system";
import type { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import styledComponents from "styled-components";

import { ReactComponent as SpaIcon } from "../../../public/material-icons/spa.svg";
import type { EcoRecommendation } from "../../service/eco";

export type EcoModalProps = {
  children: ReactNode;
  className?: string;
  onClose: () => void;
  open: boolean;
  title: string;
  emptyTitle: string;
  emptyText: string;
  buttonText: string;
  labelDevices: string;
  labelRecommendation: string;
  recommendations: EcoRecommendation[];
};

const StyledItemContainer = styledComponents.div`
  height: 560px;
  width: 600px;
  display: flex;
  flex-direction: column;
  gap: ${space(10)};
  margin-top: ${space(3)};
  padding-bottom: ${space(4)};
  overflow-y: scroll;
  &::-webkit-scrollbar {
    display: none;
  }
  > * {
    width: 100%;
    border-top: 1px solid ${theme.colorPalette.grey200};
    padding-top: ${space(4)};
  }
`;

const StyledButtonContainer = styledComponents.div`
  display: flex;
  gap: ${space(4)};
  justify-content: flex-end;
  border-top: 1px solid ${theme.colorPalette.grey200};
  padding: ${space(4)} 0;
  padding-bottom: 0;
`;

export const EcoModal: FC<EcoModalProps> = ({
  buttonText,
  className,
  emptyText,
  emptyTitle,
  labelDevices,
  labelRecommendation,
  onClose,
  open,
  recommendations,
  title,
}) => {
  const { t } = useTranslation("eco");
  return (
    <Modal className={className} onClose={onClose} open={open}>
      <Typography size="xl" weight="semiBold">
        {title}
      </Typography>
      <StyledItemContainer>
        {recommendations.map((recommendation) => (
          <FlexBox
            flexDirection="column"
            gap={space(4)}
            key={recommendation.type}
          >
            <FlexBox gap={space(10)}>
              <FlexBox flexDirection="column" gap={space(1)} width="20%">
                <Typography size="xs" weight="semiBold">
                  {labelRecommendation}
                </Typography>
                <FlexBox gap={space(1)}>
                  {Array.from({ length: recommendation.rating }).map(
                    (_, index) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <SpaIcon color={theme.color.success} key={index} />
                    )
                  )}
                </FlexBox>
              </FlexBox>
              <FlexBox width="80%">
                <Typography whiteSpace="pre-wrap">
                  {t(recommendation.message)}
                </Typography>
              </FlexBox>
            </FlexBox>
            {recommendation.devices && (
              <FlexBox gap={space(10)}>
                <FlexBox width="20%">
                  <Typography size="xs" weight="semiBold" whiteSpace="nowrap">
                    {labelDevices}
                  </Typography>
                </FlexBox>
                <FlexBox flexWrap="wrap" gap={space(2)} width="80%">
                  {recommendation.devices.map((d) => (
                    <Tag key={d.id}>{d.name}</Tag>
                  ))}
                </FlexBox>
              </FlexBox>
            )}
          </FlexBox>
        ))}
        {recommendations.length === 0 && (
          <FlexBox
            alignItems="center"
            flexDirection="column"
            gap={space(4)}
            height="100%"
            justifyContent="center"
            width="50%"
          >
            <Typography size="lg" weight="semiBold">
              {emptyTitle}
            </Typography>
            <FlexBox>
              <SpaIcon color={theme.color.success} fontSize="30px" />
              <SpaIcon color={theme.color.success} fontSize="30px" />
              <SpaIcon color={theme.color.success} fontSize="30px" />
            </FlexBox>
            <FlexBox alignItems="center" justifyContent="center" width="50%">
              <Typography size="sm" textAlign="center">
                {emptyText}
              </Typography>
            </FlexBox>
          </FlexBox>
        )}
        <FlexBox flexDirection="column" gap={space(4)}>
          <FlexBox alignItems="center" gap={space(10)}>
            <FlexBox flexDirection="column" gap={space(1)}>
              <FlexBox gap={space(1)} width={space(20)}>
                <FlexBox alignItems="center">
                  <img height={64} src="gb-logo.png" />
                </FlexBox>
              </FlexBox>
            </FlexBox>
            <Typography whiteSpace="pre-wrap">
              {t("greenbasics.text")}
              <br />
              {t("greenbasics.mail")}
            </Typography>
            <FlexBox alignItems="center">
              <Qrcode size={64} value={t("greenbasics.link")} />
            </FlexBox>
          </FlexBox>
        </FlexBox>
      </StyledItemContainer>
      <StyledButtonContainer>
        <Button onClick={onClose} variant="primary">
          {buttonText}
        </Button>
      </StyledButtonContainer>
    </Modal>
  );
};
